/**
 * IXD 편집 세션 사용 hook.
 *
 * - store에서 cellChanges/showRawValueInEdit/액션 노출 (현재 fsType 기준)
 * - 저장/다운로드 mutation 정의 (mutation key 공유 → 화면 간 busy 동기화)
 * - 저장은 부분 저장: 유효한 변경만 저장하고 거부된 변경은 수정 상태로 남긴다
 * - onSaveSuccess 주입으로 compare가 화면 전용 preview 재조회 추가 가능
 *
 * 저장은 두 구간으로 나뉜다:
 * - saveInFlight   : PATCH 요청 구간. 전체 화면 dimmer + 버튼 잠금.
 * - isReflecting   : 서버 preview 재조회 구간(백그라운드). **버튼 잠금만** — dimmer 금지.
 *
 * 재조회가 리포트 전체를 재합성하느라 수 초씩 걸리는데, 예전엔 이 구간까지 mutation에
 * 묶여 있어 화면 전체가 그 시간만큼 잠겼다. 지금은 PATCH가 끝나면 mutation이 끝나고
 * 재조회는 뒤에서 돈다 — 그 사이 사용자는 셀 편집을 계속할 수 있다.
 *
 * reportId가 null이면 mutation은 호출 시점에 throw. 호출부가 반드시 가드한다.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import {
  useMutation,
  useQueryClient,
  useIsMutating,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { downloadModifiedIxd, saveIxdChanges } from "@/apis/ixd-editor/route";
import type {
  FsType,
  IxdChunk,
  IxdDownloadFile,
} from "@/apis/ixd-editor/types";
import queries from "@/queries";
import {
  ixdEditorDownloadMutationKey,
  ixdEditorSaveMutationKey,
} from "./mutation-keys";
import {
  buildCellIdentityIndex,
  buildCellIndex,
  type CellChangeOrigin,
  parseIdentityChangeKey,
} from "@/utils/ixd-cell-model";
import {
  buildIdentityAwareCellChangePlan,
  type CellEditInput,
} from "./cell-change-plan";
import { useEditSessionStore } from "./edit-session-provider";
import {
  summarizeRejections,
  validateAllChanges,
  type RejectedCellChange,
  type ValidatedChanges,
} from "./validate";
import {
  buildSavedCellChangeIndex,
  hasUnguardedCellChanges,
  type SavedCellChange,
} from "./edit-session-store";

/** 저장할 수 있는 변경이 하나도 없어 요청 자체를 보내지 않은 실패. */
class RejectedOnlySaveError extends Error {}

/** 거절 없음 — 매번 새 빈 배열을 만들면 소비처의 "새 저장 결과" 판정이 깨진다. */
const NO_REJECTIONS: readonly RejectedCellChange[] = [];

let noopMutationKeySeed = 0;

const createNoopMutationKeys = () => {
  noopMutationKeySeed += 1;
  return {
    save: ["ixd-editor", "save", "__noop__", noopMutationKeySeed] as const,
    download: [
      "ixd-editor",
      "download",
      "__noop__",
      noopMutationKeySeed,
    ] as const,
  };
};

interface UseIxdEditSessionInput {
  reportId: string | null;
  /**
   * 화면이 거절 안내 UI(어느 셀이 왜 막혔는지)를 직접 그릴 때 true.
   * 같은 말을 토스트로 한 번 더 하지 않는다 — 토스트가 안내 위를 덮기도 한다.
   * 안내 UI가 없는 화면은 토스트가 유일한 통보 수단이라 기본값은 false다.
   */
  showsRejectionGuide?: boolean;
  fsType: FsType;
  consolidatedChunks: IxdChunk[] | undefined;
  separateChunks: IxdChunk[] | undefined;
  /**
   * 저장 검증 cell index 의 진실 공급원 — 문서 전체 chunk.
   *
   * 화면이 문서의 **부분집합**만 그리는 경우(워크북 비교는 매칭된 표만 그린다)에 넘긴다.
   * 안 넘기면 화면에 없는 표의 변경이 index 미매칭으로 저장에서 빠진다 — 워크북을
   * 고르기 전에 한 편집이 조용히 사라지는 경로가 된다.
   */
  documentChunks?: {
    consolidated: IxdChunk[] | undefined;
    separate: IxdChunk[] | undefined;
  };
  /** 저장 성공 후 추가 서버 프리뷰 재조회. 완료될 때까지 Promise를 반환한다. */
  onSaveSuccess?: () => void | Promise<void>;
}

export const useIxdEditSession = ({
  reportId,
  showsRejectionGuide = false,
  fsType,
  consolidatedChunks,
  separateChunks,
  documentChunks,
  onSaveSuccess,
}: UseIxdEditSessionInput) => {
  const queryClient = useQueryClient();
  // 편집 핸들러(useCallback)에서 최신 재조회 상태를 보기 위한 미러 + 그 구간의 편집
  // 횟수. 계측 전용이라 렌더에는 쓰지 않는다 — 렌더가 보는 값은 isReflecting state다.
  const isReflectingRef = useRef(false);
  const reflectEditCountRef = useRef(0);
  const noopMutationKeysRef = useRef<ReturnType<
    typeof createNoopMutationKeys
  > | null>(null);
  if (!noopMutationKeysRef.current) {
    noopMutationKeysRef.current = createNoopMutationKeys();
  }
  const noopMutationKeys = noopMutationKeysRef.current;

  // store state
  const allCellChanges = useEditSessionStore((s) => s.allCellChanges);
  const allChangeOrigins = useEditSessionStore((s) => s.allChangeOrigins);
  const undoStackLength = useEditSessionStore((s) => s.undoStack.length);
  const showRawValueInEdit = useEditSessionStore((s) => s.showRawValueInEdit);

  // store actions
  const replaceCellValuesRaw = useEditSessionStore((s) => s.replaceCellValues);
  const resetCellValueRaw = useEditSessionStore((s) => s.resetCellValue);
  const resetFsTypeChangesRaw = useEditSessionStore(
    (s) => s.resetFsTypeChanges,
  );
  const resetAllChanges = useEditSessionStore((s) => s.resetAllChanges);
  const clearSavedChanges = useEditSessionStore((s) => s.clearSavedChanges);
  const pushUndoMarker = useEditSessionStore((s) => s.pushUndoMarker);
  const undo = useEditSessionStore((s) => s.undo);
  const toggleShowRawValueInEdit = useEditSessionStore(
    (s) => s.toggleShowRawValueInEdit,
  );

  // 현재 토글 fsType의 변경만 노출. fsType 라우팅이 chunk 기준으로 결정되므로
  // 화면에 보이는 chunk(= 토글 fsType chunks)의 셀 변경은 모두 이 슬라이스에
  // 들어 있다. 반대편 슬롯의 변경을 같이 노출하면 양쪽 화면에서 같은 셀이
  // 변경된 것처럼 잘못 표시된다.
  const cellChanges = allCellChanges[fsType];
  const changeOrigins = allChangeOrigins[fsType];
  const unsavedCounts = useMemo(() => {
    const consolidated = Object.keys(allCellChanges.consolidated).length;
    const separate = Object.keys(allCellChanges.separate).length;
    return {
      consolidated,
      separate,
      /** 현재 보이는 fsType의 변경 개수 */
      current: fsType === "consolidated" ? consolidated : separate,
      /** 양 fsType 합산 */
      total: consolidated + separate,
    };
  }, [allCellChanges, fsType]);
  const hasUnsavedChanges = unsavedCounts.total > 0;

  // 화면 진입 시에는 현재 보이는 fsType의 identity만 만든다. 큰 IXD에서 양쪽
  // cell/identity index를 모두 선계산하면 첫 렌더 전에 전체 데이터를 네 번 순회한다.
  // 저장 검증용 cell index는 실제 저장/다운로드 시점에 buildValidated에서 만든다.
  const currentChunks =
    fsType === "consolidated" ? consolidatedChunks : separateChunks;
  const currentIdentityIndex = useMemo(
    () => buildCellIdentityIndex(currentChunks ?? []),
    [currentChunks],
  );

  // store 키가 이미 ChangeKey라 identity 슬라이스를 그대로 노출하면 된다. 예전처럼
  // cellId → identity 역조회를 하면 축만 다른 셀의 identity를 집는다.
  const identityChanges = useMemo(() => {
    const map = new Map<string, string>();
    for (const [changeKey, value] of Object.entries(cellChanges)) {
      const identityKey = parseIdentityChangeKey(changeKey);
      if (identityKey) map.set(identityKey, value);
    }
    return map;
  }, [cellChanges]);

  // chunkId → fsType 매핑. 편집 시점에 셀이 속한 chunk를 알면 fsType이 자명하므로,
  // 이 map을 renderer에 주입해 chunk마다 핸들러를 fsType-binding한다.
  const chunkFsTypeById = useMemo(() => {
    const map = new Map<string, FsType>();
    for (const c of consolidatedChunks ?? []) map.set(c.id, "consolidated");
    for (const c of separateChunks ?? []) map.set(c.id, "separate");
    return map;
  }, [consolidatedChunks, separateChunks]);

  const createCellChangePlan = useCallback(
    (entries: CellEditInput[]) =>
      buildIdentityAwareCellChangePlan({
        entries,
        allCellChanges,
      }),
    [allCellChanges],
  );

  // 편집 호출은 셀이 속한 chunk의 fsType과, 셀이 자기 dlView에서 해석한 identityKey를
  // 명시한다(IxdPreviewRenderer가 chunk별로 핸들러를 binding). identityKey를 셀에서
  // 받아야 하는 이유: cellId에는 축이 없어 위층에서 역조회하면 축만 다른 다른 표의
  // fact를 집는다. 같은 identity의 편집은 한 ChangeKey로 합쳐져 화면과 저장이 하나의
  // 논리값을 본다.
  const setCellValue = useCallback(
    (
      targetFsType: FsType,
      cellId: string,
      newValue: string,
      identityKey?: string,
      locationUid?: string,
    ) => {
      const plan = createCellChangePlan([
        {
          fsType: targetFsType,
          cellId,
          newValue,
          identityKey,
          locationUid,
        },
      ]);
      if (isReflectingRef.current) reflectEditCountRef.current += 1;
      replaceCellValuesRaw(plan.entries, plan.cellsToRemove, "manual");
    },
    [createCellChangePlan, replaceCellValuesRaw],
  );
  // mixed batch: 한 paste/delete가 양쪽 fsType 셀에 걸쳐도 한 undo 그룹.
  // 반환값은 충돌로 제외된 entries 인덱스 — 나머지는 반영된다(부분 반영).
  const setCellValuesWithOrigin = useCallback(
    (entries: CellEditInput[], origin: CellChangeOrigin): number[] => {
      if (entries.length === 0) return [];
      const plan = createCellChangePlan(entries);
      const rejectedIndexes = plan.conflicts.flatMap((c) => c.entryIndexes);
      if (plan.conflicts.length > 0) {
        console.error("[DEBUG] IXD 일괄 편집 — 연동 셀 입력값 충돌", {
          conflicts: plan.conflicts,
        });
        // 값 반영 버튼은 부분 반영 토스트 + 셀 강조로 이미 안내한다 — 중복 토스트 금지.
        if (origin !== "value-apply") {
          toast.warning(
            "같은 항목으로 연동된 셀에 서로 다른 값이 들어와 해당 셀은 반영하지 않았습니다.",
          );
        }
      }
      if (plan.entries.length === 0) return rejectedIndexes;
      if (isReflectingRef.current) reflectEditCountRef.current += 1;
      replaceCellValuesRaw(plan.entries, plan.cellsToRemove, origin);
      return rejectedIndexes;
    },
    [createCellChangePlan, replaceCellValuesRaw],
  );
  // 사용자가 직접 붙여넣은 값은 셀 우상단 수동 변경 표식을 남긴다.
  const setCellValues = useCallback(
    (entries: CellEditInput[]): number[] =>
      setCellValuesWithOrigin(entries, "manual"),
    [setCellValuesWithOrigin],
  );
  // 값 반영 버튼은 값만 변경하고 수동 변경 표식은 남기지 않는다.
  const setAppliedCellValues = useCallback(
    (entries: CellEditInput[]): number[] =>
      setCellValuesWithOrigin(entries, "value-apply"),
    [setCellValuesWithOrigin],
  );
  const resetCellValue = useCallback(
    (targetFsType: FsType, cellId: string) =>
      resetCellValueRaw(targetFsType, cellId),
    [resetCellValueRaw],
  );
  const resetFsTypeChanges = useCallback(
    () => resetFsTypeChangesRaw(fsType),
    [fsType, resetFsTypeChangesRaw],
  );

  // 다운로드 URL navigation은 beforeunload를 발생시킨다. onSuccess에서 편집 상태를
  // 초기화한 직후 동기적으로 navigate하면, 미저장 가드의 리스너 제거(useEffect
  // cleanup)가 아직 flush되지 않았을 수 있어 경고가 뜨는 레이스가 있다.
  // URL을 상태로 넘겨 effect에서 navigate하면, 같은 commit에서 React가 cleanup을
  // 전부 실행한 뒤 effect를 실행하므로 리스너 제거가 navigate보다 먼저임이 보장된다.
  const [pendingDownloadUrl, setPendingDownloadUrl] = useState<string | null>(
    null,
  );
  useEffect(() => {
    if (!pendingDownloadUrl) return;
    setPendingDownloadUrl(null);
    window.location.href = pendingDownloadUrl;
  }, [pendingDownloadUrl]);

  // 백그라운드 재조회 구간. 버튼 잠금 전용 — 전체 화면 dimmer에는 절대 연결하지 않는다.
  //
  // "재조회 중인가"와 "그때 저장된 변경 목록"은 항상 함께 세워지고 함께 내려간다.
  // 두 값을 따로 두면(state + ref) 그 짝이 주석에만 존재해 한쪽만 건드리는 순간
  // 이탈 가드가 조용히 stale해진다 — 한 state로 묶어 구조가 계약을 강제하게 한다.
  // pendingSaved를 개수로만 비교하면 "저장분 제거 + 신규 입력"이 상쇄돼 가드가
  // 풀리므로 (fsType, changeKey, value)까지 대조한다.
  const [reflect, setReflect] = useState<{
    pendingSaved: readonly SavedCellChange[];
  } | null>(null);

  /**
   * 마지막 저장에서 거절된 변경 목록 그대로. 요약 문구(summarizeRejections)는 셀
   * 위치를 버리므로, "어느 표 어느 셀이 왜 막혔는지"를 그리려면 원본이 필요하다.
   * 저장할 때마다 교체되고(거절 0건이면 빈 목록), 화면이 안내를 닫으면 비운다.
   */
  const [rejections, setRejections] =
    useState<readonly RejectedCellChange[]>(NO_REJECTIONS);
  const clearRejections = useCallback(() => setRejections(NO_REJECTIONS), []);
  const isReflecting = reflect !== null;

  const previewQueryKey = reportId
    ? queries.ixdEditor(reportId)._ctx.preview().queryKey
    : null;

  const refetchPreview = async () => {
    if (!previewQueryKey) return;
    await queryClient.refetchQueries(
      {
        queryKey: previewQueryKey,
        exact: true,
        type: "active",
      },
      { throwOnError: true },
    );
  };

  // 저장/다운로드 헬퍼 — 검증·부분 저장 분류는 validateAllChanges가 전담
  const buildValidated = (): ValidatedChanges =>
    validateAllChanges({
      consolidatedChanges: allCellChanges.consolidated,
      separateChanges: allCellChanges.separate,
      // 화면 chunk 가 아니라 문서 전체로 검증한다 — 화면 밖 표의 변경도 저장 대상이다.
      consolidatedIndex: buildCellIndex(
        documentChunks?.consolidated ?? consolidatedChunks ?? [],
      ),
      separateIndex: buildCellIndex(
        documentChunks?.separate ?? separateChunks ?? [],
      ),
    });

  /**
   * 시스템 원인(index 미매칭 등)으로 제외된 변경을 Sentry에 보고한다.
   * 사용자 입력 문제(kind: "user-input")는 toast 안내로 충분하므로 보내지 않는다.
   * Slack 알림은 코드가 아니라 Sentry alert rule로 연결한다.
   */
  const reportSystemRejections = (validated: ValidatedChanges) => {
    const systemRejections = validated.rejected.filter(
      (r) => r.kind === "system",
    );
    if (systemRejections.length === 0) return;
    Sentry.captureMessage("[IXD] 저장 검증 — 시스템 원인으로 제외된 변경", {
      level: "error",
      extra: {
        reportId,
        rejections: systemRejections.map(
          ({ fsType: rejectedFsType, changeKey, value, reason }) => ({
            fsType: rejectedFsType,
            changeKey,
            value,
            reason,
          }),
        ),
        savedCount: validated.items.length,
        rejectedCount: validated.rejected.length,
      },
    });
  };

  /**
   * 저장·다운로드 공통 진입점 — 검증하고, 시스템 거절은 Sentry로, 거절 목록은 화면으로 넘긴다.
   * 세 단계를 묶어두는 이유: 통보를 빼먹은 경로가 생기면 사용자는 "왜 안 되는지" 알 수 없다
   * (다운로드 경로가 정확히 그랬다). 검증하는 경로는 전부 이 함수를 쓴다.
   */
  const validateAndPublishRejections = (): ValidatedChanges => {
    const validated = buildValidated();
    reportSystemRejections(validated);
    setRejections(
      validated.rejected.length > 0 ? validated.rejected : NO_REJECTIONS,
    );
    return validated;
  };

  /**
   * 저장 후 서버 preview 재조회 — 백그라운드. mutation은 이미 끝난 상태다.
   *
   * 이 함수를 await하는 호출자는 없다(다운로드 경로 포함). 여기서 던지면
   * unhandled rejection이 되므로 Promise.allSettled로 거부를 만들지 않는다.
   */
  // 재조회 도중 새 저장이 시작되면 이전 세대의 완료 처리가 새 상태를 꺼버리는 것을 막는다.
  // 현재는 저장·다운로드 진입점이 전부 isActionLocked로 잠긴 버튼뿐이라 세대가 갈릴 수
  // 없다(= 아래 gen 비교는 항상 참). 단축키 등 버튼 외 경로가 생기면 그때 살아난다.
  const reflectGenRef = useRef(0);

  const startReflect = async (validated: ValidatedChanges) => {
    const gen = ++reflectGenRef.current;
    reflectEditCountRef.current = 0;
    isReflectingRef.current = true;
    setReflect({ pendingSaved: validated.savedCellRefs });

    // 두 재조회를 분리해서 본다. 오버레이 정리 가능 여부는 preview 성공에만 달렸다
    // — baseline이 새 값으로 교체돼야 "저장된 값과 같은 셀"을 지우는 것이 안전하다.
    // onSaveSuccess(화면 전용 재조회)의 실패는 오버레이와 무관하므로 안내만 하고
    // 정리는 진행한다. Promise.all로 묶으면 이 둘을 구분할 수 없다.
    const [sideEffect, preview] = await Promise.allSettled([
      onSaveSuccess?.(),
      refetchPreview(),
    ]);

    // 재조회 도중 새 저장이 시작됐다면 그쪽 세대가 상태를 소유한다 — 건드리지 않는다.
    // 편집 카운터도 세대별이 아니라 훅 인스턴스 공유라, 세대가 갈린 상황에서 보고하면
    // 다음 세대가 세고 있는 값을 이전 세대가 대신 보고하게 된다. 같이 가둔다.
    if (gen === reflectGenRef.current) {
      isReflectingRef.current = false;
      setReflect(null);

      // 재조회 구간에 실제로 편집이 일어났다면 그 편집의 undo 기록은 아래
      // clearSavedChanges에서 통째로 버려진다(edit-session-store.ts의 ponytail 주석).
      // 천장을 올릴지 판단하려면 이 일이 실제로 벌어지는지부터 알아야 한다.
      //
      // 제거 기한: 2026-08-19. 이 PR이 "재조회 중에도 편집 가능"을 목표로 하므로
      // 이 이벤트는 정상 흐름에서 발생한다 — 영구 계측이 아니라 2주짜리 관측이다.
      // 기한이 지나면 둘 중 하나로 반드시 종결한다:
      //   발생함  → undo 보존(저장 시점 이후 entry만 남기기)으로 승격하고 계측 제거
      //   발생 안함 → 천장을 그대로 두고 계측만 제거
      if (reflectEditCountRef.current > 0) {
        Sentry.captureMessage("[IXD] 재조회 중 편집 발생 — undo 기록 소실", {
          level: "info",
          extra: { reportId, editCount: reflectEditCountRef.current },
        });
      }
    }

    if (preview.status === "rejected") {
      // preview 재조회 실패 시에는 편집 오버레이를 그대로 유지한다 — 사용자가 보던
      // 수정값이 갑자기 사라지지 않게 하고, 새로고침을 안내한다.
      console.error("[DEBUG] IXD 저장 후 미리보기 갱신 실패", preview.reason);
      toast.warning(
        "저장은 완료됐지만 최신 미리보기를 불러오지 못했습니다. 새로고침해 주세요.",
      );
      return;
    }
    if (sideEffect.status === "rejected") {
      // preview는 새 값이지만 화면 전용 데이터(비교 결과 등)만 옛 세대로 남는다.
      console.error(
        "[DEBUG] IXD 저장 후 화면 전용 재조회 실패",
        sideEffect.reason,
      );
      toast.warning(
        "저장은 완료됐지만 화면 일부가 최신 상태로 갱신되지 않았습니다. 새로고침해 주세요.",
      );
    }
    // 서버 응답으로 화면 데이터가 교체된 뒤에만 로컬 편집 오버레이를 제거한다.
    // 전량 삭제(resetAllChanges)를 쓰지 않는 이유: 재조회가 도는 동안 사용자가
    // 새로 입력한 값까지 지워진다. 저장한 값이 그대로 남아 있는 셀만 정리한다.
    clearSavedChanges(validated.savedCellRefs);
    if (validated.rejected.length > 0 && !showsRejectionGuide) {
      toast.warning(
        `${validated.rejected.length}건은 저장하지 못해 수정 상태로 남겨두었습니다 — ${summarizeRejections(validated.rejected)}`,
      );
    }
  };

  const saveMutation = useMutation({
    mutationKey: reportId
      ? ixdEditorSaveMutationKey(reportId)
      : noopMutationKeys.save,
    mutationFn: async () => {
      if (!reportId) throw new Error("reportId 없이 저장할 수 없습니다.");
      const validated = validateAndPublishRejections();
      // 저장 가능한 변경이 하나도 없으면 요청 없이 실패 처리 (거부 사유 안내)
      if (validated.items.length === 0 && validated.rejected.length > 0) {
        throw new RejectedOnlySaveError(
          summarizeRejections(validated.rejected),
        );
      }
      await saveIxdChanges(reportId, validated.items);
      return validated;
    },
    // await하지 않는다 — PATCH가 끝나는 즉시 mutation을 종료시켜 화면 잠금을 푼다.
    // 재조회는 isReflecting으로 넘어가 뒤에서 돈다.
    onSuccess: (validated) => {
      // 저장 결과 안내는 여기 한 곳에서만 한다 — 저장 버튼을 가진 화면이 하나가 아니다.
      if (validated.rejected.length === 0) {
        toast.success("수정된 내용이 저장되었습니다.");
      } else if (showsRejectionGuide && validated.items.length > 0) {
        // 부분 저장. 거절 사유·위치는 안내 popover 가 말하므로 여기서는 "그래도
        // 이만큼은 저장됐다"만 알린다 — 둘 다 빠지면 사용자는 저장이 통째로 실패한
        // 줄 안다. 안내 UI 가 없는 화면은 startReflect 의 경고가 그 역할을 한다.
        toast.success(`${validated.items.length}개 항목을 저장했습니다.`);
      }
      void startReflect(validated);
    },
    onError: (error) => {
      // 거절뿐이라 보내지 않은 저장은 화면의 안내 UI가 이미 말하고 있다.
      // 네트워크·서버 실패는 안내 UI가 모르므로 토스트를 유지한다.
      if (showsRejectionGuide && error instanceof RejectedOnlySaveError) return;
      toast.error(
        error instanceof Error
          ? error.message
          : "저장할 수 없는 입력값이 있습니다.",
      );
    },
  });

  /** 마지막으로 성공한 다운로드 파일 — 다운로드 완료 패널의 표시 조건 */
  const [downloadedFile, setDownloadedFile] = useState<IxdDownloadFile | null>(
    null,
  );
  // 요청이 도는 중에 패널을 닫았으면 그 요청 결과로 패널을 되살리지 않는다.
  // (파일 저장 자체는 그대로 진행한다 — 사용자가 요청한 다운로드이므로.)
  const downloadDismissedRef = useRef(false);

  const downloadMutation = useMutation({
    mutationKey: reportId
      ? ixdEditorDownloadMutationKey(reportId)
      : noopMutationKeys.download,
    mutationFn: async () => {
      if (!reportId) throw new Error("reportId 없이 다운로드할 수 없습니다.");
      downloadDismissedRef.current = false;

      let validated: ValidatedChanges | null = null;
      let savedSucceeded = false;
      if (hasUnsavedChanges) {
        // 거절 목록은 저장 경로와 똑같이 화면에 넘긴다 — 요약 토스트 한 줄로 끝내면
        // 어느 표 어느 셀이 왜 막혔는지 알 길이 없다.
        validated = validateAndPublishRejections();
        // 다운로드는 부분 저장을 허용하지 않는다 — 수정분이 누락된 파일이 나가면 안 된다.
        if (validated.rejected.length > 0) {
          // 저장과 달리 토스트를 억제하지 않는다 — "파일이 안 나갔다"는 안내 popover가
          // 말해주지 않는 사실이다(popover 문구는 "저장되지 않은 값이 N건"). 대신 사유
          // 요약은 빼고, 다운로드 버튼과 떨어져 있는 저장 버튼 쪽 안내로 길만 이어준다.
          throw new Error(
            showsRejectionGuide
              ? "저장할 수 없는 수정값이 있어 다운로드를 진행하지 못했습니다. 저장 버튼 옆 안내에서 막힌 셀을 확인해 주세요."
              : `저장할 수 없는 수정값이 있어 다운로드를 진행하지 못했습니다: ${summarizeRejections(validated.rejected)}`,
          );
        }
        try {
          await saveIxdChanges(reportId, validated.items);
          savedSucceeded = true;
        } catch (error) {
          const message =
            error instanceof Error
              ? `저장에 실패해 다운로드를 진행하지 못했습니다: ${error.message}`
              : "저장에 실패해 다운로드를 진행하지 못했습니다.";
          throw new Error(message);
        }
      }
      try {
        const result = await downloadModifiedIxd(reportId);
        return { result, validated, savedSucceeded };
      } catch (error) {
        // 저장만 성공한 상태 — 저장 결과 정리는 백그라운드로 넘기고 에러만 던짐
        if (savedSucceeded && validated) {
          void startReflect(validated);
        }
        throw error;
      }
    },
    onSuccess: ({ result: downloadFile, validated }) => {
      if (validated) {
        // 재조회를 기다리지 않고 곧바로 navigate한다. 오버레이는 아직 남아 있지만
        // startReflect가 동기 구간에서 pendingSaved를 세워두므로 이탈 가드가
        // 이를 미저장으로 오인하지 않는다 (hasUnguardedChanges 참조).
        void startReflect(validated);
      } else {
        // 미저장 변경이 없던 다운로드 — 정리할 오버레이가 없어 재조회만 돈다.
        // 조용히 삼키면 화면이 옛 데이터인 채로 남으므로 저장 경로와 같게 안내한다.
        void refetchPreview().catch((error) => {
          console.error("[DEBUG] IXD 다운로드 후 미리보기 갱신 실패", error);
          toast.warning(
            "최신 미리보기를 불러오지 못했습니다. 새로고침해 주세요.",
          );
        });
      }
      // mutation.data 는 재요청(pending)에서 undefined 로 되돌아가므로 패널 유지용으로 쓸 수 없다.
      if (!downloadDismissedRef.current) setDownloadedFile(downloadFile);
      setPendingDownloadUrl(downloadFile.downloadUrl);
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "다운로드에 실패했습니다.",
      );
    },
  });

  // reportId가 null이면 인스턴스별 noop key로 다른 편집 세션의 busy 상태와 섞이지 않게 한다.
  const saveInFlight =
    useIsMutating({
      mutationKey: reportId
        ? ixdEditorSaveMutationKey(reportId)
        : noopMutationKeys.save,
    }) > 0;
  const downloadInFlight =
    useIsMutating({
      mutationKey: reportId
        ? ixdEditorDownloadMutationKey(reportId)
        : noopMutationKeys.download,
    }) > 0;
  // 전체 화면 dimmer 전용 — 사용자가 실제로 기다려야 하는 요청 구간에만 걸린다.
  // isReflecting을 여기 넣으면 백그라운드로 돌린 의미가 사라진다.
  const isBusy = saveInFlight || downloadInFlight;
  // 버튼 잠금 전용 — 재조회 중 중복 저장/초기화/다운로드를 막는다.
  const isActionLocked = isBusy || isReflecting;
  // 이탈 가드용 — 재조회 중인 변경은 서버 저장이 이미 끝났으므로 미저장이 아니다.
  // 재조회 구간에서는 전체 변경 수와 저장분 수가 둘 다 최대인데(대량 붙여넣기 직후
  // 저장), 이 PR이 노리는 것이 바로 그 구간의 편집이다 — 편집 한 번마다 전체 대조가
  // 돌지 않도록 Set 인덱스로 만들어 두고 메모한다.
  const pendingSavedIndex = useMemo(
    () => buildSavedCellChangeIndex(reflect?.pendingSaved ?? []),
    [reflect],
  );
  const hasUnguardedChanges = useMemo(
    () => hasUnguardedCellChanges(allCellChanges, pendingSavedIndex),
    [allCellChanges, pendingSavedIndex],
  );

  return {
    // state
    cellChanges,
    changeOrigins,
    chunkFsTypeById,
    cellIdentityByNormalizedCellId:
      currentIdentityIndex.identityKeyByNormalizedCellId,
    identityLocationsByKey: currentIdentityIndex.identityLocationsByKey,
    identityChanges,
    hasUnsavedChanges,
    /** 마지막 저장에서 거절된 변경 — 셀 위치·사유를 그대로 담고 있다. */
    rejections,
    /** 거절 안내를 닫는다(사용자가 명시적으로 닫았을 때). */
    clearRejections,
    /** 이탈 경고 대상 — 재조회 중(저장은 끝난) 변경은 제외한다. */
    hasUnguardedChanges,
    unsavedCounts,
    showRawValueInEdit,
    undoCount: undoStackLength,

    // actions (fsType-bound)
    setCellValue,
    setCellValues,
    setAppliedCellValues,
    resetCellValue,
    resetFsTypeChanges,
    resetAllChanges,
    pushUndoMarker,
    undo,
    toggleShowRawValueInEdit,

    // mutations
    saveMutation,
    downloadMutation,
    /** 마지막으로 성공한 다운로드 파일 (없으면 null) */
    downloadedFile,
    clearDownloadedFile: () => {
      downloadDismissedRef.current = true;
      setDownloadedFile(null);
    },

    // busy
    saveInFlight,
    downloadInFlight,
    /** 저장 후 백그라운드 재조회 중 */
    isReflecting,
    /** 전체 화면 dimmer 조건 (PATCH·다운로드 요청 구간만) */
    isBusy,
    /** 저장·초기화·다운로드 버튼 잠금 조건 (재조회 구간 포함) */
    isActionLocked,
  };
};
